export interface ExportColumn {
  field: string
  header: string
  numeric?: boolean
  sub_type?: string
  currency?: boolean
  percentage?: boolean
}

export type ExportRow = Record<string, unknown>

export interface SheetInput {
  name: string
  data: ExportRow[]
  columns: ExportColumn[]
}

export interface ExportProgress {
  phase: 'preparing' | 'rendering' | 'writing' | 'done' | 'error'
  pct?: number
}

export interface ExportTelemetry {
  fileFormat: 'xlsx' | 'pdf' | 'csv'
  featureName?: string
  userId?: number | string
}

export interface XlsxOptions {
  currencySuffix?: string
  onProgress?: (progress: ExportProgress) => void
  telemetry?: ExportTelemetry
  onTelemetry?: (event: ExportTelemetry) => void
}

export type CaptureSource = HTMLElement | readonly HTMLElement[]

export type PdfInput = CaptureSource | HTMLCanvasElement

export interface CaptureOptions {
  scale?: number
  backgroundColor?: string
  /** Additional selectors removed from the export clone. `[data-pdf-ignore]` is always removed. */
  ignoreSelectors?: readonly string[]
}

export interface CaptureResult {
  canvas: HTMLCanvasElement
  breakpoints: readonly number[]
}

export interface PdfWatermark {
  imageSrc?: string
  opacity?: number
}

export interface PdfOptions {
  orientation?: 'portrait' | 'landscape'
  /** PDF page margin in millimetres. */
  margin?: number
  watermark?: PdfWatermark | null
  capture?: CaptureOptions
  waitFor?: () => Promise<void>
  onProgress?: (progress: ExportProgress) => void
  telemetry?: ExportTelemetry
  onTelemetry?: (event: ExportTelemetry) => void
}

export type ExportAdapterErrorCode = 'capture-failed' | 'pdf-export-failed' | 'vendor-load-failed'

/** Typed failure boundary for lazy file-generation vendors and browser capture. */
export class ExportAdapterError extends Error {
  constructor(
    readonly code: ExportAdapterErrorCode,
    message: string,
    cause?: unknown,
  ) {
    super(message, { cause })
    this.name = 'ExportAdapterError'
  }
}
